import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Download } from 'lucide-react'
import {
  Button,
  Chip,
  DataTable,
  DateRangePicker,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
} from '@/components/ui'
import { rollupExportCsvUrl, useForecastRollup } from '../api/queries'
import type { MatchType, RollupRow } from '../api/types'

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function startOfYearIso(d = new Date()): string {
  return isoDate(new Date(d.getFullYear(), 0, 1))
}

function endOfYearIso(d = new Date()): string {
  return isoDate(new Date(d.getFullYear(), 11, 31))
}

const MATCH_LABELS: Record<MatchType, string> = {
  exact: 'Exact',
  partial_overlap: 'Partial overlap',
}

/**
 * Anti-fabrication rule (mirrors CLAUDE.md's diagnostic-fabrication
 * guardrail for CARLA, applied here in spirit): `total_qty` is always
 * the server's raw sum — never prorated in this UI. The `partial_overlap`
 * badge is the visual cue that a shown total may include a lump-sum line
 * whose period is WIDER than the requested window, so a viewer never
 * mistakes it for a precise figure scoped exactly to the window.
 */
export function ForecastRollupPage() {
  const [periodFrom, setPeriodFrom] = useState(() => startOfYearIso())
  const [periodTo, setPeriodTo] = useState(() => endOfYearIso())

  const filters = useMemo(() => ({ periodFrom, periodTo }), [periodFrom, periodTo])
  const { data: rows, isLoading, isError } = useForecastRollup(filters)

  const totalLines = rows?.reduce((sum, r) => sum + r.line_count, 0) ?? 0
  const distinctCustomers = new Set(rows?.map((r) => r.customer_id)).size
  const distinctProducts = new Set(rows?.map((r) => r.product_id)).size

  const columns = useMemo<ColumnDef<RollupRow, unknown>[]>(
    () => [
      {
        id: 'customer_name',
        header: 'Customer',
        accessorKey: 'customer_name',
        cell: (ctx) => ctx.getValue() ?? <span className="text-tertiary">—</span>,
      },
      {
        id: 'product',
        header: 'Product',
        accessorFn: (r) => r,
        cell: (ctx) => {
          const row = ctx.getValue() as RollupRow
          return (
            <span className="flex flex-col">
              <span>{row.product_name ?? `#${row.product_id}`}</span>
              {row.product_default_code ? (
                <span className="text-role-metadata text-tertiary">{row.product_default_code}</span>
              ) : null}
            </span>
          )
        },
      },
      {
        id: 'total_qty',
        header: 'Total forecasted qty',
        accessorKey: 'total_qty',
        cell: (ctx) => Number(ctx.getValue() as string).toLocaleString(),
      },
      {
        id: 'match_type',
        header: 'Coverage',
        accessorKey: 'match_type',
        cell: (ctx) => {
          const v = ctx.getValue() as MatchType
          return (
            <Chip size="xs" variant={v === 'exact' ? 'neutral' : 'warning'}>
              {MATCH_LABELS[v]}
            </Chip>
          )
        },
      },
      {
        id: 'line_count',
        header: 'Lines',
        accessorKey: 'line_count',
      },
    ],
    [],
  )

  return (
    <div data-density="comfortable" className="flex h-full flex-col">
      <PageHeaderBand
        title="Procurement Rollup"
        description="Forecasted demand rolled up by customer and product for the selected window. Totals are exactly what reps entered — never prorated across periods."
      >
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <DateRangePicker
            value={{ from: periodFrom, to: periodTo }}
            onChange={(range) => {
              setPeriodFrom(range.from)
              setPeriodTo(range.to)
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => window.open(rollupExportCsvUrl(filters), '_blank')}
          >
            <Download size={14} /> Export CSV
          </Button>
        </div>
      </PageHeaderBand>

      <MetricStrip>
        <MetricCard label="Forecast lines" value={totalLines} />
        <MetricCard label="Customers" value={distinctCustomers} />
        <MetricCard label="Products" value={distinctProducts} />
      </MetricStrip>

      <div className="min-h-0 flex-1 p-4">
        {isError ? (
          <EmptyState
            variant="error"
            title="Couldn't load the rollup"
            description="The /api/forecasts/rollup endpoint returned an error. Try again."
          />
        ) : (
          <DataTable<RollupRow>
            data={rows ?? []}
            columns={columns}
            loading={isLoading}
            getRowId={(r) => `${r.customer_id}-${r.product_id}`}
            moduleId="demand-forecast-rollup"
            maxHeight="100%"
            minTableWidth={900}
            emptyState={
              <EmptyState
                variant="no-data"
                title="No forecast lines in this window"
                description="Try widening the date range, or check back once reps have entered forecasts."
              />
            }
          />
        )}
      </div>
    </div>
  )
}
